import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, BookmarkPlus, PenLine, Check, X } from "lucide-react";
import { toast } from "sonner";
import {
  syntaxColors,
  applySyntaxHighlighting,
  getSyntaxPatterns,
} from "@/utils/silae-editor-utils";

interface SilaeCodeBlockProps {
  code: string;
  lang?: string;
  onSave?: () => void;
  /** Correction d'un code généré par l'IA : (codeFinal) => … ; alimente
   *  l'apprentissage. Non fourni = pas de bouton « Corriger ». */
  onCorrect?: (finalCode: string) => void | Promise<void>;
}

// Patterns statiques (pas de variables SILAE dynamiques côté chat)
const STATIC_PATTERNS = [
  { pattern: /(\/\/.*$)/gm, type: "comments" },
  { pattern: /\b(Begin|End)\b/gi, type: "structure" },
  { pattern: /\b(If|Then|Else|Endif|SelectCase|Case|Default|EndSelect)\b/gi, type: "control" },
  ...getSyntaxPatterns([]),
];

export const SilaeCodeBlock: React.FC<SilaeCodeBlockProps> = ({
  code,
  lang,
  onSave,
  onCorrect,
}) => {
  const { t } = useTranslation();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(code);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const lines = (editing ? draft : code).split("\n");

  const highlighted = useMemo(
    () =>
      applySyntaxHighlighting(code, syntaxColors, STATIC_PATTERNS).map((el) => (
        <span key={el.key} className={el.className}>
          {el.text}
        </span>
      )),
    [code],
  );

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    toast.success(t("aiAgent.codeBlock.codeCopied"));
  };

  const startEdit = () => {
    setDraft(code);
    setEditing(true);
    // Focus en fin de texte au prochain tick.
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(code);
  };

  const dirty = draft.trim().length > 0 && draft !== code;

  const saveCorrection = async () => {
    if (!dirty || !onCorrect) return;
    setSaving(true);
    try {
      await onCorrect(draft);
      toast.success(t("aiAgent.codeBlock.correctionSaved"));
      setEditing(false);
    } catch {
      toast.error(t("aiAgent.codeBlock.correctionFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded-lg border overflow-hidden shadow-sm text-xs font-mono my-1 transition-colors duration-200 ${
        editing ? "border-primary/40 ring-1 ring-primary/20" : "border-border"
      }`}
    >
      {/* Titre style IDE */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-muted border-b border-border">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
          </div>
          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            {lang === "silae" || !lang ? "SILAE" : lang.toUpperCase()}
            {editing && (
              <span className="ml-2 text-primary normal-case tracking-normal">
                · {t("aiAgent.codeBlock.correctingBadge")}
              </span>
            )}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button
                onClick={saveCorrection}
                disabled={!dirty || saving}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="h-3 w-3" />
                {t("aiAgent.codeBlock.saveCorrection")}
              </button>
              <button
                onClick={cancelEdit}
                disabled={saving}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium text-muted-foreground hover:bg-muted/80 transition-colors cursor-pointer"
              >
                <X className="h-3 w-3" />
                {t("aiAgent.codeBlock.cancel")}
              </button>
            </>
          ) : (
            <>
              <span className="text-[10px] text-muted-foreground">
                {lines.length > 1
                  ? t("aiAgent.codeBlock.lines_other", { count: lines.length })
                  : t("aiAgent.codeBlock.lines_one", { count: lines.length })}
              </span>
              {onCorrect && (
                <button
                  onClick={startEdit}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-semibold bg-primary text-primary-foreground shadow-sm ring-1 ring-primary/30 hover:bg-primary/90 hover:shadow transition-all cursor-pointer"
                  aria-label={t("aiAgent.codeBlock.correct")}
                >
                  <PenLine className="h-3.5 w-3.5" />
                  {t("aiAgent.codeBlock.correct")}
                </button>
              )}
              {onSave && (
                <button
                  onClick={onSave}
                  className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors cursor-pointer"
                  aria-label={t("aiAgent.codeBlock.saveToLibrary")}
                >
                  <BookmarkPlus className="h-3 w-3" />
                  {t("aiAgent.codeBlock.save")}
                </button>
              )}
              <button
                onClick={handleCopy}
                className="p-1 rounded hover:bg-muted/80 transition-colors cursor-pointer"
                aria-label={t("aiAgent.codeBlock.copyCode")}
              >
                <Copy className="h-3 w-3 text-muted-foreground" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Bandeau d'aide en mode correction — honnête, pas marketing */}
      {editing && (
        <div className="px-3 py-1.5 bg-primary/5 border-b border-primary/15 text-[11px] text-muted-foreground">
          {t("aiAgent.codeBlock.correctHint")}
        </div>
      )}

      {/* Zone de code avec numéros de ligne */}
      <div className="flex overflow-x-auto bg-background">
        {/* Numéros de ligne (alignés : même leading que le textarea) */}
        <div
          className="flex flex-col shrink-0 px-2 py-3 bg-muted/40 border-r border-border text-muted-foreground select-none"
          aria-hidden
        >
          {lines.map((_, i) => (
            <span
              key={i}
              className="text-right leading-[1.7] min-w-[1.5rem] block text-[11px]"
            >
              {i + 1}
            </span>
          ))}
        </div>

        {editing ? (
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            rows={lines.length}
            className="flex-1 px-4 py-3 bg-background text-foreground whitespace-pre leading-[1.7] text-[12px] font-mono resize-none outline-none overflow-hidden"
          />
        ) : (
          <pre className="flex-1 px-4 py-3 text-foreground whitespace-pre leading-[1.7] overflow-x-auto text-[12px]">
            {highlighted}
          </pre>
        )}
      </div>

      {/* Barre de statut */}
      <div className="flex items-center gap-3 px-3 py-1 bg-muted border-t border-border text-[10px] text-muted-foreground">
        <span>SILAE</span>
        <span>UTF-8</span>
      </div>
    </div>
  );
};
